import { useState, useEffect, useCallback, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Pencil, X, Check, Milestone, CalendarDays, ChevronUp, ChevronDown, Briefcase, Monitor } from "lucide-react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Pagination } from "@/components/Pagination";
import { ConfirmDeleteDialog } from "@/components/ConfirmDeleteDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  "Not Started": { color: "#94a3b8", bg: "bg-slate-100 dark:bg-slate-800", text: "text-slate-600 dark:text-slate-300", border: "border-slate-200 dark:border-slate-700" },
  "On Track": { color: "#22c55e", bg: "bg-green-50 dark:bg-green-900/30", text: "text-green-700 dark:text-green-400", border: "border-green-200 dark:border-green-800" },
  "At Risk": { color: "#eab308", bg: "bg-yellow-50 dark:bg-yellow-900/30", text: "text-yellow-700 dark:text-yellow-400", border: "border-yellow-200 dark:border-yellow-800" },
  "Off Track": { color: "#ef4444", bg: "bg-red-50 dark:bg-red-900/30", text: "text-red-700 dark:text-red-400", border: "border-red-200 dark:border-red-800" },
  "Completed": { color: "#3b82f6", bg: "bg-blue-50 dark:bg-blue-900/30", text: "text-blue-700 dark:text-blue-400", border: "border-blue-200 dark:border-blue-800" },
};

const STATUS_OPTIONS = ["Not Started", "On Track", "At Risk", "Off Track", "Completed"];
const MILESTONE_TYPES = [
  { value: "business", label: "Business", icon: Briefcase, color: "text-blue-600 dark:text-blue-400", bg: "bg-blue-50 dark:bg-blue-900/30", border: "border-blue-200 dark:border-blue-800" },
  { value: "it", label: "IT", icon: Monitor, color: "text-purple-600 dark:text-purple-400", bg: "bg-purple-50 dark:bg-purple-900/30", border: "border-purple-200 dark:border-purple-800" },
  { value: "both", label: "Both", icon: null, color: "text-slate-600 dark:text-slate-300", bg: "bg-slate-100 dark:bg-slate-800", border: "border-slate-200 dark:border-slate-700" },
];

const fmt = (d) => {
  if (!d) return "";
  return new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const laneColors = [
  { bg: "bg-slate-50 dark:bg-slate-800/50", border: "border-slate-100 dark:border-slate-700" },
  { bg: "bg-blue-50/40 dark:bg-blue-900/20", border: "border-blue-100/60 dark:border-blue-800/40" },
  { bg: "bg-amber-50/30 dark:bg-amber-900/20", border: "border-amber-100/50 dark:border-amber-800/40" },
  { bg: "bg-emerald-50/30 dark:bg-emerald-900/20", border: "border-emerald-100/50 dark:border-emerald-800/40" },
  { bg: "bg-purple-50/30 dark:bg-purple-900/20", border: "border-purple-100/50 dark:border-purple-800/40" },
  { bg: "bg-rose-50/30 dark:bg-rose-900/20", border: "border-rose-100/50 dark:border-rose-800/40" },
];

const GanttChart = ({ milestones, onMoveUp, onMoveDown }) => {
  const range = useMemo(() => {
    const dates = milestones.flatMap((m) => [m.target_start, m.target_end, m.actual_end].filter(Boolean));
    if (!dates.length) return { min: new Date(), max: new Date(), span: 1 };
    const sorted = dates.map((d) => new Date(d + "T00:00:00")).sort((a, b) => a - b);
    const min = new Date(sorted[0]);
    min.setDate(min.getDate() - 5);
    const max = new Date(sorted[sorted.length - 1]);
    max.setDate(max.getDate() + 10);
    return { min, max, span: Math.max(1, (max - min) / (1000 * 60 * 60 * 24)) };
  }, [milestones]);

  const toPercent = (dateStr) => {
    if (!dateStr) return 0;
    const d = new Date(dateStr + "T00:00:00");
    return Math.max(0, Math.min(100, ((d - range.min) / (1000 * 60 * 60 * 24) / range.span) * 100));
  };

  const months = useMemo(() => {
    const result = [];
    const d = new Date(range.min);
    d.setDate(1);
    while (d <= range.max) {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const localStr = `${y}-${m}-01`;
      const pct = toPercent(localStr);
      if (pct >= 0 && pct <= 100) result.push({ label: d.toLocaleDateString("en-US", { month: "short", year: "numeric" }), pct });
      d.setMonth(d.getMonth() + 1);
    }
    return result;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range]);

  const todayStr = (() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, "0");
    const d = String(now.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  })();
  const todayPct = toPercent(todayStr);
  const todayLabel = new Date(todayStr + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" });

  if (!milestones.length) return null;

  return (
    <Card className="card-modern mb-8" data-testid="gantt-chart">
      <CardContent className="p-6">
        <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100 font-['Outfit'] mb-1">Timeline</h3>
        <p className="text-[11px] text-slate-400 dark:text-slate-300 mb-5">Use arrows to reorder milestone priority</p>
        <div>
          {/* Milestone rows with aligned headers */}
          <div className="pt-2">
            {/* Month headers row - aligned with bar area */}
            <div className="flex items-center gap-0 mb-2">
              <div className="w-7 flex-shrink-0" />
              <div className="w-36 flex-shrink-0 pr-3" />
              <div className="flex-1 relative h-8 border-b border-slate-100 dark:border-slate-700">
                {months.map((m, i) => (
                  <div key={i} className="absolute text-[10px] font-semibold text-slate-400 dark:text-slate-200 uppercase tracking-wider" style={{ left: `${m.pct}%` }}>
                    {m.label}
                  </div>
                ))}
              </div>
            </div>

            {milestones.map((m, index) => {
              const cfg = statusConfig[m.status] || statusConfig["Not Started"];
              const startPct = toPercent(m.target_start);
              const endPct = toPercent(m.target_end);
              const barWidth = Math.max(2, endPct - startPct);
              const hasActual = !!m.actual_end;
              const actualEndPct = hasActual ? toPercent(m.actual_end) : 0;
              const actualWidth = hasActual ? Math.max(2, actualEndPct - startPct) : 0;
              const isOverdue = hasActual && new Date(m.actual_end) > new Date(m.target_end);
              const lane = laneColors[index % laneColors.length];
              const isFirst = index === 0;
              const isLast = index === milestones.length - 1;

              return (
                <div key={m.id} className="flex items-center gap-0 rounded-lg mb-1 relative" data-testid={`gantt-row-${m.id}`}>
                  <div className="w-7 flex-shrink-0 flex flex-col items-center gap-0.5" data-testid={`gantt-arrows-${m.id}`}>
                    <button
                      onClick={() => onMoveUp(index)}
                      disabled={isFirst}
                      className={`p-0.5 rounded transition-colors ${isFirst ? "text-slate-200 dark:text-slate-700 cursor-default" : "text-slate-400 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700"}`}
                      title="Move up"
                      data-testid={`gantt-up-${m.id}`}
                    >
                      <ChevronUp className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => onMoveDown(index)}
                      disabled={isLast}
                      className={`p-0.5 rounded transition-colors ${isLast ? "text-slate-200 dark:text-slate-700 cursor-default" : "text-slate-400 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700"}`}
                      title="Move down"
                      data-testid={`gantt-down-${m.id}`}
                    >
                      <ChevronDown className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="w-36 flex-shrink-0 pr-3 py-2 group relative">
                    <p className="text-xs font-semibold text-slate-700 dark:text-slate-200 truncate cursor-default" title={m.title}>{m.title}</p>
                    <Badge className={`${cfg.bg} ${cfg.text} ${cfg.border} border text-[9px] mt-0.5`}>{m.status}</Badge>
                    {/* Tooltip on hover */}
                    <div className="absolute left-0 bottom-full mb-1 z-50 hidden group-hover:block">
                      <div className="bg-slate-900 text-white text-xs px-2.5 py-1.5 rounded shadow-lg max-w-xs whitespace-normal break-words">
                        {m.title}
                      </div>
                    </div>
                  </div>
                  <div className={`flex-1 relative h-12 rounded-md border ${lane.border} ${lane.bg}`}>
                    {/* Today line inside each bar row */}
                    {todayPct > 0 && todayPct < 100 && (
                      <div className="absolute top-0 bottom-0 w-px bg-red-400/50 z-10" style={{ left: `${todayPct}%` }} />
                    )}
                    {/* Status bar */}
                    <div className="absolute top-2 h-3 rounded-full opacity-90" title={`Target: ${fmt(m.target_start)} – ${fmt(m.target_end)}`}
                      style={{ left: `${startPct}%`, width: `${barWidth}%`, backgroundColor: cfg.color }} />
                    {/* Actual bar */}
                    {hasActual && (
                      <div className={`absolute bottom-2 h-3 rounded-full ${isOverdue ? "bg-red-400" : "bg-emerald-400"} opacity-80`}
                        title={`Actual: ${fmt(m.target_start)} – ${fmt(m.actual_end)}`}
                        style={{ left: `${startPct}%`, width: `${actualWidth}%` }} />
                    )}
                  </div>
                </div>
              );
            })}

            {/* Today label row - aligned with bar area */}
            {todayPct > 0 && todayPct < 100 && (
              <div className="flex items-center gap-0 mt-1">
                <div className="w-7 flex-shrink-0" />
                <div className="w-36 flex-shrink-0 pr-3" />
                <div className="flex-1 relative h-5">
                  <div className="absolute -translate-x-1/2 text-[9px] font-bold text-red-500 dark:text-red-400 bg-red-50 dark:bg-red-900/30 px-1.5 py-0.5 rounded whitespace-nowrap" style={{ left: `${todayPct}%` }}>TODAY {todayLabel}</div>
                </div>
              </div>
            )}
          </div>

          {/* Legend */}
          <div className="flex items-center gap-5 mt-6 pt-4 border-t border-slate-100 dark:border-slate-700 flex-wrap">
            <span className="text-[10px] text-slate-400 dark:text-slate-500 font-semibold uppercase tracking-wider mr-1">Status Bar:</span>
            <div className="flex items-center gap-1.5"><div className="w-5 h-2.5 rounded-full bg-slate-400 opacity-90" /><span className="text-[10px] text-slate-500 dark:text-slate-400 font-medium">Not Started</span></div>
            <div className="flex items-center gap-1.5"><div className="w-5 h-2.5 rounded-full bg-green-500 opacity-90" /><span className="text-[10px] text-slate-500 dark:text-slate-400 font-medium">On Track</span></div>
            <div className="flex items-center gap-1.5"><div className="w-5 h-2.5 rounded-full bg-yellow-500 opacity-90" /><span className="text-[10px] text-slate-500 dark:text-slate-400 font-medium">At Risk</span></div>
            <div className="flex items-center gap-1.5"><div className="w-5 h-2.5 rounded-full bg-red-500 opacity-90" /><span className="text-[10px] text-slate-500 dark:text-slate-400 font-medium">Off Track</span></div>
            <div className="flex items-center gap-1.5"><div className="w-5 h-2.5 rounded-full bg-blue-500 opacity-90" /><span className="text-[10px] text-slate-500 dark:text-slate-400 font-medium">Completed</span></div>
            <span className="text-slate-200 dark:text-slate-700 mx-1">|</span>
            <span className="text-[10px] text-slate-400 dark:text-slate-500 font-semibold uppercase tracking-wider mr-1">Actual:</span>
            <div className="flex items-center gap-1.5"><div className="w-5 h-2.5 rounded-full bg-emerald-400 opacity-80" /><span className="text-[10px] text-slate-500 dark:text-slate-400 font-medium">Met Target</span></div>
            <div className="flex items-center gap-1.5"><div className="w-5 h-2.5 rounded-full bg-red-400 opacity-80" /><span className="text-[10px] text-slate-500 dark:text-slate-400 font-medium">Passed Target</span></div>
            <span className="text-slate-200 dark:text-slate-700 mx-1">|</span>
            <div className="flex items-center gap-1.5"><div className="w-px h-4 bg-red-400" /><span className="text-[10px] text-slate-500 dark:text-slate-400 font-medium">Today</span></div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export const MilestonesPanel = () => {
  const { selectedProduct } = useAuth();
  const productId = selectedProduct?.id;
  const [milestones, setMilestones] = useState([]);
  const [msPage, setMsPage] = useState(1);
  const MS_PAGE_SIZE = 10;
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [formData, setFormData] = useState({ title: "", target_start: "", target_end: "", actual_end: "", status: "Not Started", milestone_type: "both" });

  const fetchMilestones = useCallback(async () => {
    try { 
      const params = productId ? { product_id: productId } : {};
      const r = await axios.get(`${API}/milestones`, { params }); 
      setMilestones(r.data); 
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [productId]);

  useEffect(() => { fetchMilestones(); }, [fetchMilestones]);

  const addMilestone = async () => {
    if (!formData.title || !formData.target_start || !formData.target_end) return;
    try { await axios.post(`${API}/milestones`, { ...formData, product_id: productId }); setFormData({ title: "", target_start: "", target_end: "", actual_end: "", status: "Not Started", milestone_type: "both" }); setShowForm(false); await fetchMilestones(); } catch (e) { console.error(e); }
  };

  const saveEdit = async (mid) => {
    try { await axios.patch(`${API}/milestones/${mid}`, editData); setEditingId(null); await fetchMilestones(); } catch (e) { console.error(e); }
  };

  const deleteMilestone = async (mid) => {
    try { await axios.delete(`${API}/milestones/${mid}`); await fetchMilestones(); } catch (e) { console.error(e); }
  };

  const moveMilestone = async (fromIdx, toIdx) => {
    if (toIdx < 0 || toIdx >= milestones.length) return;
    const reordered = Array.from(milestones);
    const [moved] = reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, moved);
    setMilestones(reordered);
    try {
      await axios.post(`${API}/milestones/reorder`, { items: reordered.map((m, i) => ({ id: m.id, order: i })) });
    } catch (e) {
      console.error(e);
      await fetchMilestones();
    }
  };

  const counts = {
    total: milestones.length,
    completed: milestones.filter((m) => m.status === "Completed").length,
    onTime: milestones.filter((m) => m.status === "On Track" || m.status === "In Progress").length,
    atRisk: milestones.filter((m) => m.status === "At Risk").length,
    offTrack: milestones.filter((m) => m.status === "Off Track" || m.status === "Delayed").length,
    notStarted: milestones.filter((m) => m.status === "Not Started").length,
  };

  return (
    <>
    <div data-testid="milestones-panel">
      <p className="text-xs font-semibold tracking-widest uppercase text-[#F59E0B] mb-3">Planning</p>
      <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 dark:text-slate-100 font-['Outfit']">Milestones</h1>
      <p className="text-base text-slate-500 dark:text-slate-400 mt-2">Launch dates, target completion, and actual end dates</p>
      <Separator className="my-8 bg-slate-200 dark:bg-slate-700" />

      {/* Summary */}
      <div className="flex items-center gap-3 mb-6 flex-wrap" data-testid="milestone-counters">
        <Badge className="bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800">{counts.total} Total</Badge>
        {counts.notStarted > 0 && <Badge className="bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700">{counts.notStarted} Not Started</Badge>}
        {counts.onTime > 0 && <Badge className="bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800">{counts.onTime} On Track</Badge>}
        {counts.atRisk > 0 && <Badge className="bg-yellow-50 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 border border-yellow-200 dark:border-yellow-800">{counts.atRisk} At Risk</Badge>}
        {counts.offTrack > 0 && <Badge className="bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800">{counts.offTrack} Off Track</Badge>}
        {counts.completed > 0 && <Badge className="bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border border-blue-200 dark:border-blue-800">{counts.completed} Completed</Badge>}
      </div>

      {/* Gantt Chart */}
      <GanttChart milestones={milestones} onMoveUp={(i) => moveMilestone(i, i - 1)} onMoveDown={(i) => moveMilestone(i, i + 1)} />

      {/* Milestone Cards */}
      {!loading && (
        <div className="flex flex-col gap-3" data-testid="milestones-list">
          {milestones.slice((msPage - 1) * MS_PAGE_SIZE, msPage * MS_PAGE_SIZE).map((m) => {
            const cfg = statusConfig[m.status] || statusConfig["Not Started"];
            const isEditing = editingId === m.id;
            return (
              <Card key={m.id} className="card-modern" data-testid={`milestone-card-${m.id}`}>
                <CardContent className="p-5">
                  {isEditing ? (
                    <div className="space-y-3" data-testid={`milestone-edit-${m.id}`}>
                      <Input value={editData.title ?? m.title} onChange={(e) => setEditData((p) => ({ ...p, title: e.target.value }))} className="font-semibold dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100" data-testid="milestone-edit-title" />
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                        <div><label className="text-[10px] text-slate-500 dark:text-slate-400 font-medium block mb-1">Target Start</label><Input type="date" value={editData.target_start ?? m.target_start} onChange={(e) => setEditData((p) => ({ ...p, target_start: e.target.value }))} className="h-8 text-xs dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100" /></div>
                        <div><label className="text-[10px] text-slate-500 dark:text-slate-400 font-medium block mb-1">Target End</label><Input type="date" value={editData.target_end ?? m.target_end} onChange={(e) => setEditData((p) => ({ ...p, target_end: e.target.value }))} className="h-8 text-xs dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100" /></div>
                        <div><label className="text-[10px] text-slate-500 dark:text-slate-400 font-medium block mb-1">Actual End</label><Input type="date" value={editData.actual_end ?? m.actual_end} onChange={(e) => setEditData((p) => ({ ...p, actual_end: e.target.value }))} className="h-8 text-xs dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100" /></div>
                        <div><label className="text-[10px] text-slate-500 dark:text-slate-400 font-medium block mb-1">Status</label>
                          <Select value={editData.status ?? m.status} onValueChange={(val) => setEditData((p) => ({ ...p, status: val }))}>
                            <SelectTrigger className="h-8 text-xs dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100"><SelectValue /></SelectTrigger>
                            <SelectContent className="dark:bg-slate-800 dark:border-slate-700">{STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s} className="dark:text-slate-100 dark:hover:bg-slate-700">{s}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div><label className="text-[10px] text-slate-500 dark:text-slate-400 font-medium block mb-1">Type</label>
                          <Select value={editData.milestone_type ?? m.milestone_type ?? "both"} onValueChange={(val) => setEditData((p) => ({ ...p, milestone_type: val }))}>
                            <SelectTrigger className="h-8 text-xs dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100" data-testid="milestone-edit-type"><SelectValue /></SelectTrigger>
                            <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                              <SelectItem value="business" className="dark:text-slate-100 dark:hover:bg-slate-700">Business</SelectItem>
                              <SelectItem value="it" className="dark:text-slate-100 dark:hover:bg-slate-700">IT</SelectItem>
                              <SelectItem value="both" className="dark:text-slate-100 dark:hover:bg-slate-700">Both</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => saveEdit(m.id)} className="h-7 bg-green-600 hover:bg-green-700 text-white text-xs" data-testid="milestone-edit-save"><Check className="w-3.5 h-3.5 mr-1" />Save</Button>
                        <Button size="sm" variant="outline" onClick={() => setEditingId(null)} className="h-7 text-xs dark:bg-slate-800 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"><X className="w-3.5 h-3.5 mr-1" />Cancel</Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2.5 mb-2">
                          <Milestone className="w-4 h-4" style={{ color: cfg.color }} />
                          <h3 className="text-base font-semibold text-slate-800 dark:text-slate-100 font-['Outfit']">{m.title}</h3>
                          <Badge className={`${cfg.bg} ${cfg.text} ${cfg.border} border text-[11px]`}>{m.status}</Badge>
                          {(() => {
                            const mt = MILESTONE_TYPES.find(t => t.value === (m.milestone_type || "both"));
                            return mt ? <Badge className={`${mt.bg} ${mt.color} ${mt.border} border text-[11px]`}>{mt.label}</Badge> : null;
                          })()}
                        </div>
                        <div className="flex items-center gap-5 flex-wrap">
                          <div className="flex items-center gap-1.5"><CalendarDays className="w-3 h-3 text-slate-400 dark:text-slate-500" /><span className="text-xs text-slate-500 dark:text-slate-400">Target: <span className="font-medium text-slate-700 dark:text-slate-200">{fmt(m.target_start)} – {fmt(m.target_end)}</span></span></div>
                          {m.actual_end && (
                            <div className="flex items-center gap-1.5">
                              <CalendarDays className="w-3 h-3 text-slate-400 dark:text-slate-500" />
                              <span className={`text-xs ${new Date(m.actual_end) > new Date(m.target_end) ? "text-red-600 dark:text-red-400 font-semibold" : "text-green-600 dark:text-green-400 font-medium"}`}>Actual End: {fmt(m.actual_end)}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        <button onClick={() => { setEditingId(m.id); setEditData({}); }} className="p-1.5 rounded text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700/50 transition-colors" data-testid={`milestone-edit-btn-${m.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                        <button onClick={() => setDeleteTarget({ id: m.id, label: m.title })} className="p-1.5 rounded text-slate-500 dark:text-slate-400 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors" data-testid={`milestone-delete-btn-${m.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {milestones.length === 0 && !loading && (
        <div className="text-center py-12 text-slate-400 dark:text-slate-500"><Milestone className="w-10 h-10 mx-auto mb-3 opacity-40" /><p className="text-sm">No milestones yet. Add your first launch date milestone.</p></div>
      )}
      {!loading && milestones.length > 0 && (
        <Pagination currentPage={msPage} totalItems={milestones.length} pageSize={MS_PAGE_SIZE} onPageChange={setMsPage} />
      )}

      {showForm ? (
        <Card className="mt-6 card-modern" data-testid="add-milestone-form">
          <CardContent className="p-6">
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200 font-['Outfit'] mb-4">Add Milestone</h3>
            <div className="space-y-3">
              <Input placeholder="Milestone title (e.g. UAT Sign-off, Go-Live)" value={formData.title} onChange={(e) => setFormData((p) => ({ ...p, title: e.target.value }))} className="dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100" data-testid="milestone-title-input" />
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <div><label className="text-[10px] text-slate-500 dark:text-slate-400 font-medium block mb-1">Target Start</label><Input type="date" value={formData.target_start} onChange={(e) => setFormData((p) => ({ ...p, target_start: e.target.value }))} className="h-8 text-xs dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100" data-testid="milestone-start-input" /></div>
                <div><label className="text-[10px] text-slate-500 dark:text-slate-400 font-medium block mb-1">Target End</label><Input type="date" value={formData.target_end} onChange={(e) => setFormData((p) => ({ ...p, target_end: e.target.value }))} className="h-8 text-xs dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100" data-testid="milestone-end-input" /></div>
                <div><label className="text-[10px] text-slate-500 dark:text-slate-400 font-medium block mb-1">Actual End</label><Input type="date" value={formData.actual_end} onChange={(e) => setFormData((p) => ({ ...p, actual_end: e.target.value }))} className="h-8 text-xs dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100" data-testid="milestone-actual-input" /></div>
                <div><label className="text-[10px] text-slate-500 dark:text-slate-400 font-medium block mb-1">Status</label>
                  <Select value={formData.status} onValueChange={(val) => setFormData((p) => ({ ...p, status: val }))}>
                    <SelectTrigger className="h-8 text-xs dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100" data-testid="milestone-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="dark:bg-slate-800 dark:border-slate-700">{STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s} className="dark:text-slate-100 dark:hover:bg-slate-700">{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><label className="text-[10px] text-slate-500 dark:text-slate-400 font-medium block mb-1">Type</label>
                  <Select value={formData.milestone_type} onValueChange={(val) => setFormData((p) => ({ ...p, milestone_type: val }))}>
                    <SelectTrigger className="h-8 text-xs dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100" data-testid="milestone-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                      <SelectItem value="business" className="dark:text-slate-100 dark:hover:bg-slate-700">Business</SelectItem>
                      <SelectItem value="it" className="dark:text-slate-100 dark:hover:bg-slate-700">IT</SelectItem>
                      <SelectItem value="both" className="dark:text-slate-100 dark:hover:bg-slate-700">Both</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={addMilestone} disabled={!formData.title || !formData.target_start || !formData.target_end} className="bg-[#0F172A] hover:bg-slate-800 text-white dark:bg-[#00D4C8] dark:hover:bg-[#00B8AE] dark:text-slate-900" data-testid="milestone-submit-btn"><Plus className="w-4 h-4 mr-2" />Add Milestone</Button>
                <Button variant="outline" onClick={() => setShowForm(false)} className="dark:bg-slate-800 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700">Cancel</Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Button onClick={() => setShowForm(true)} className="mt-6 shadow-sm bg-slate-700 hover:bg-slate-600 text-white font-medium" data-testid="add-milestone-btn"><Plus className="w-4 h-4 mr-2" />Add Milestone</Button>
      )}
    </div>
    <ConfirmDeleteDialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} onConfirm={() => deleteMilestone(deleteTarget?.id)} title="Delete Milestone" description={`Are you sure you want to delete "${deleteTarget?.label || "this milestone"}"? This action cannot be undone.`} />
    </>
  );
};
